import { FeatureFlagContext } from '@hopin-team/provider-feature-flag';
import { isFulfilled } from '@reduxjs/toolkit';
import { useContext, useEffect, useMemo, useState } from 'react';

import { EventRegistrationStatusTypes } from '../features/purchase-tickets/purchase-actions';
import { loadWidgetThunkAction } from '../redux/load-widget-thunk';
import { useAppDispatch } from '../redux/store';
import { logError } from '../utils/logging';
import { getUrlDetails } from '../utils/url';

type LoadingStatus = 'initial' | 'loading' | 'complete';
interface LoadEventState {
  eventLoadingStatus: LoadingStatus;
  eventLoadingError: boolean;
  eventRegistrationStatus: Nullable<EventRegistrationStatusTypes>;
  organizationId: Nullable<number>;
  organizationExternalId: Nullable<string>;
  widgetDisabled: boolean;
}

const initialState: LoadEventState = {
  eventLoadingStatus: 'initial',
  eventLoadingError: false,
  organizationId: null,
  organizationExternalId: null,
  widgetDisabled: false,
  eventRegistrationStatus: null,
};

export const useLoadWidget = () => {
  const dispatch = useAppDispatch();
  const { identifyUser, isLoading: flagsLoading } =
    useContext(FeatureFlagContext);

  const [
    {
      eventLoadingStatus,
      organizationId,
      organizationExternalId,
      ...eventState
    },
    setEventState,
  ] = useState(initialState);

  const [isLoaded, setIsLoaded] = useState(false);
  const [flagsLoadingStatus, setFlagsLoadingStatus] =
    useState<LoadingStatus>('initial');

  const urlDetails = useMemo(getUrlDetails, []);

  // STEP 1 - load event details
  useEffect(() => {
    if (eventLoadingStatus !== 'initial') return;

    const loadEventDetails = async () => {
      setEventState(prevState => ({
        ...prevState,
        eventLoadingStatus: 'loading',
      }));

      const response = await dispatch(loadWidgetThunkAction(urlDetails));

      if (isFulfilled(response)) {
        setEventState(prevState => ({
          ...prevState,
          organizationId: response.payload.eventDetails.settings.organizationId,
          organizationExternalId:
            response.payload.eventDetails.settings.organizationExternalId,
          eventRegistrationStatus: response.payload.registrationStatus,
          eventLoadingStatus: 'complete',
        }));
        return;
      }

      setEventState(prevState => ({
        ...prevState,
        eventLoadingError: true,
        eventLoadingStatus: 'complete',
      }));
      setIsLoaded(true);
    };

    loadEventDetails();
  }, [dispatch, eventLoadingStatus, urlDetails]);

  // STEP 2 - loading org specific flags
  useEffect(() => {
    if (
      eventLoadingStatus !== 'complete' ||
      flagsLoadingStatus !== 'initial' ||
      !organizationId ||
      !organizationExternalId
    )
      return;

    const loadOrganizationSpecificFlags = async () => {
      try {
        setFlagsLoadingStatus('loading');
        await identifyUser({
          key: 'organizationId',
          custom: {
            organizationId: Number(organizationId),
            organizationExternalId: organizationExternalId,
            eventSlug: urlDetails.slug,
          },
        });
      } catch (err) {
        logError(err as any);
      } finally {
        setFlagsLoadingStatus('complete');
      }
    };

    loadOrganizationSpecificFlags();
  }, [
    eventLoadingStatus,
    flagsLoadingStatus,
    identifyUser,
    organizationExternalId,
    organizationId,
    urlDetails.slug,
  ]);

  // STEP 3 - complete loading
  useEffect(() => {
    if (
      isLoaded ||
      eventLoadingStatus !== 'complete' ||
      flagsLoadingStatus !== 'complete' ||
      flagsLoading
    )
      return;

    setIsLoaded(true);
  }, [eventLoadingStatus, flagsLoading, isLoaded, flagsLoadingStatus]);

  return {
    isLoaded,
    ...eventState,
  };
};
